import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getDoc, setDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Card, Button, TextField, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Tooltip, Chip, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const ATHLETE_TYPES = [
  { value: '', label: '— Any —' },
  { value: 'junior', label: 'Junior' },
  { value: 'adult', label: 'Adult' },
];

const SESSION_TYPES = [
  { value: '', label: '— Any —' },
  { value: 'junior-practice', label: 'Junior Practice' },
  { value: 'junior-private', label: 'Junior Private' },
  { value: 'junior-drop-in', label: 'Junior Drop-in' },
  { value: 'junior-evaluation', label: 'Junior Evaluation' },
  { value: 'adult-practice', label: 'Adult Practice' },
  { value: 'league', label: 'League' },
];

const SKILL_GROUPS = ['', 'Beginner', 'Intermediate', 'Advanced'];

const emptyFilter = {
  slug: '',
  name: '',
  filters: { athleteType: '', sessionType: '', skillGroup: '', headCoachId: '' },
};

export default function CalendarFiltersAdmin() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // active filter being edited
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    try {
      const [filtersSnap, coachesSnap] = await Promise.all([
        getDocs(collection(db, 'calendarFilters')),
        getDocs(collection(db, 'users')),
      ]);
      setFilters(filtersSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setCoaches(coachesSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => (u.roles || []).some(r => r === 'coach' || r === 'assistant_coach')));
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function startNew() {
    setEditing({ ...emptyFilter });
    setDialogOpen(true);
    setError('');
  }

  function startEdit(f) {
    setEditing({ ...emptyFilter, ...f, filters: { ...emptyFilter.filters, ...(f.filters || {}) } });
    setDialogOpen(true);
    setError('');
  }

  async function save() {
    if (!editing.name?.trim()) { setError('Name is required.'); return; }
    const slug = (editing.slug && slugify(editing.slug)) || slugify(editing.name);
    if (!slug) { setError('Slug could not be generated.'); return; }
    setSaving(true);
    try {
      // Uniqueness check (skip when slug unchanged from current id)
      if (slug !== editing.id) {
        const existing = await getDoc(doc(db, 'calendarFilters', slug));
        if (existing.exists()) {
          setError(`Slug "${slug}" is already in use. Pick a different name or slug.`);
          setSaving(false); return;
        }
      }
      // Strip empty filter values to keep the doc clean
      const cleanFilters = {};
      for (const [k, v] of Object.entries(editing.filters || {})) {
        if (v) cleanFilters[k] = v;
      }
      await setDoc(doc(db, 'calendarFilters', slug), {
        name: editing.name.trim(),
        slug,
        filters: cleanFilters,
      });
      // If renamed, delete old doc
      if (editing.id && editing.id !== slug) {
        await deleteDoc(doc(db, 'calendarFilters', editing.id));
      }
      setSuccess(`Saved "${editing.name}"`);
      setTimeout(() => setSuccess(''), 3000);
      setDialogOpen(false);
      setEditing(null);
      loadAll();
    } catch (e) {
      setError('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(f) {
    if (!window.confirm(`Delete the "${f.name}" filter shortcut? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'calendarFilters', f.id));
      setFilters(prev => prev.filter(x => x.id !== f.id));
    } catch (e) {
      setError('Delete failed: ' + e.message);
    }
  }

  function copyUrl(slug) {
    const url = `${window.location.origin}/calendar/${slug}`;
    navigator.clipboard.writeText(url);
    setSuccess('Link copied.');
    setTimeout(() => setSuccess(''), 2000);
  }

  function describeFilter(f) {
    const parts = [];
    const filt = f.filters || {};
    if (filt.athleteType) parts.push(filt.athleteType);
    if (filt.sessionType) parts.push(filt.sessionType.replace('-', ' '));
    if (filt.skillGroup) parts.push(filt.skillGroup);
    if (filt.headCoachId) {
      const c = coaches.find(c => c.id === filt.headCoachId);
      parts.push(c ? `${c.firstName} ${c.lastName}` : 'coach');
    }
    return parts.length === 0 ? 'All events' : parts.join(' · ');
  }

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Typography variant="h4" fontWeight="bold">📅 Calendar Filter Shortcuts</Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
            Custom URLs that filter the public calendar — e.g., /calendar/juniors
          </Typography>
        </Container>
      </Box>

      <Container sx={{ pb: 6 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography fontWeight="bold">{filters.length} Filter Shortcut{filters.length !== 1 ? 's' : ''}</Typography>
            <Button size="small" variant="contained" color="primary" startIcon={<AddIcon />} onClick={startNew}>
              Add Filter
            </Button>
          </Box>
          {filters.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">No filter shortcuts yet.</Typography>
              <Typography variant="caption">Create one to share a focused calendar URL with members.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Name</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Filters</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filters.map(f => (
                  <TableRow key={f.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{f.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>/calendar/{f.slug}</Typography>
                        <Tooltip title="Copy full URL">
                          <IconButton size="small" onClick={() => copyUrl(f.slug)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open in new tab">
                          <IconButton size="small" onClick={() => window.open(`/calendar/${f.slug}`, '_blank')}>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{describeFilter(f)}</Typography></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => startEdit(f)}><EditIcon fontSize="small" color="primary" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => remove(f)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin')}>
          Back to Admin
        </Button>
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a2940', color: 'white' }}>
          {editing?.id ? 'Edit Filter Shortcut' : 'New Filter Shortcut'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editing && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" label="Name" value={editing.name}
                           onChange={e => setEditing(p => ({ ...p, name: e.target.value }))}
                           placeholder="e.g. Junior Beginners" autoFocus />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" label="Slug (URL path)"
                           value={editing.slug}
                           onChange={e => setEditing(p => ({ ...p, slug: e.target.value }))}
                           placeholder={editing.name ? slugify(editing.name) : 'auto-generated from name'}
                           helperText={`URL: /calendar/${editing.slug || (editing.name ? slugify(editing.name) : '...')}`} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Athlete Type</InputLabel>
                  <Select label="Athlete Type" value={editing.filters.athleteType || ''}
                          onChange={e => setEditing(p => ({ ...p, filters: { ...p.filters, athleteType: e.target.value } }))}>
                    {ATHLETE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Session Type</InputLabel>
                  <Select label="Session Type" value={editing.filters.sessionType || ''}
                          onChange={e => setEditing(p => ({ ...p, filters: { ...p.filters, sessionType: e.target.value } }))}>
                    {SESSION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Skill Group</InputLabel>
                  <Select label="Skill Group" value={editing.filters.skillGroup || ''}
                          onChange={e => setEditing(p => ({ ...p, filters: { ...p.filters, skillGroup: e.target.value } }))}>
                    {SKILL_GROUPS.map(s => <MenuItem key={s} value={s}>{s || '— Any —'}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Head Coach</InputLabel>
                  <Select label="Head Coach" value={editing.filters.headCoachId || ''}
                          onChange={e => setEditing(p => ({ ...p, filters: { ...p.filters, headCoachId: e.target.value } }))}>
                    <MenuItem value="">— Any —</MenuItem>
                    {coaches.map(c => <MenuItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              {error && <Grid size={{ xs: 12 }}><Alert severity="error">{error}</Alert></Grid>}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
